import React, { useState } from "react";
import { AiOutlineFile } from "react-icons/ai";
import UserAvatar from "./UserAvatar";
import VoiceMessagePlayer from "./VoiceMessagePlayer";
import { buildImageUrl, BASE_URL, getSenderColor } from "../helper/constants";
import LocationMapPreview from "./LocationMapPreview";
import { urlify } from "../helper/convertor";
import { getUsernameFromText } from "../pages/Project/GroupChat/getUsername";
import ChatVideoBubble from "./chatMedia/ChatVideoBubble";
import { decryptMessage } from "../utils/encryptDecryptFunction";
import { markdownListMarkersToBullets } from "../utils/conversationUtils";
import "./ReplyPreview.css";

const REPLY_TRUNCATE_LENGTH = 300;

const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
};

// Chat notes (joined, left, pending, (de)activated, removed, declined) are not messages: a
// reply quoting one has nothing to show and only leaves an empty, coloured quote box.
const NOTE_FLAGS = ["joinedGroup", "leftGroup", "pendingGroup", "rejectedGroup", "userDeleted", "userDeactivated", "userReactivate"];

/**
 * True when the quoted message has something to show (and is not a chat note).
 * `decrypt` turns the stored text into plain text first: in encrypted rooms even an empty
 * text is stored encrypted (non-empty), which is how quotes of notes ended up with a box.
 */
export const isQuotableMessage = (msg, decrypt = (text) => text) => {
  if (!msg) return false;
  if (NOTE_FLAGS.some((flag) => !!msg[flag])) return false;
  let text = "";
  if (typeof msg.message === "string" && msg.message) {
    try {
      text = decrypt(msg.message) || "";
    } catch (_) {
      text = "";
    }
  }
  const plain = stripHtml(String(text));
  // Text that is still ciphertext could not be read: nothing to show either.
  const hasText = plain.length > 0 && !plain.startsWith("U2FsdGVkX1");
  // Notes carry an empty `fileUrl: []`; only a real file counts.
  const hasFile = Array.isArray(msg.fileUrl) ? msg.fileUrl.some(Boolean) : !!msg.fileUrl;
  return !!(
    hasText ||
    hasFile ||
    msg.transcribedAudio ||
    (Array.isArray(msg.contacts) && msg.contacts.length) ||
    (Array.isArray(msg.images) && msg.images.length) ||
    (Array.isArray(msg.documents) && msg.documents.length)
  );
};

const ReplyPreview = React.memo(({
  chat,
  side = "left",
  onAnchorClick,
  userId,
  strings,
  secretKey,
  chatId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const inReplyTo = chat?.inReplyTo;

  const decryptedMessage = (msg) => {
    if (!msg) return "";
    return secretKey ? decryptMessage(msg, secretKey) : msg;
  };

  if (!isQuotableMessage(inReplyTo, decryptedMessage)) return null;

  const isOwn = side === "right";
  const msgClass = isOwn ? "user-message" : "other-message";
  const senderColor = inReplyTo?.themeColor || getSenderColor(inReplyTo?.senderId);

  const renderHtml = (msg) => ({
    __html: urlify(getUsernameFromText(markdownListMarkersToBullets(decryptedMessage(msg)), strings)),
  });

  const renderTruncatedHtml = (msg) => {
    const full = markdownListMarkersToBullets(decryptedMessage(msg));
    const plain = stripHtml(urlify(getUsernameFromText(full, strings)));
    if (plain.length <= REPLY_TRUNCATE_LENGTH || expanded) {
      return { html: renderHtml(msg), needsToggle: plain.length > REPLY_TRUNCATE_LENGTH };
    }
    const truncated = plain.slice(0, REPLY_TRUNCATE_LENGTH);
    return {
      html: { __html: urlify(getUsernameFromText(truncated, strings)) },
      needsToggle: true,
    };
  };

  const senderName = inReplyTo?.senderId !== userId
    ? inReplyTo?.senderName
    : strings.you;

  return (
    <div
      className={`reply-${side} pointer${isOwn && chat.isForward ? " d-none" : ""}`}
      onClick={() => onAnchorClick(`chat-${inReplyTo.id}`)}
    >
      {isOwn && inReplyTo?.messageType !== "image" && inReplyTo?.messageType !== "audio" && (
        <div className="d-flex flex-row">
          <p className="mb-0 reply-preview__own-sender">
            {senderName}
          </p>
        </div>
      )}

      {inReplyTo?.messageType === "contact" && (
        <div className={`right_msg_reply${isOwn ? " reply-preview__contact--own" : ""}`}>
          {!isOwn && (
            <div className="right_reply_img">
              <img
                className="reply-preview__contact-avatar"
                src={inReplyTo?.contacts?.[0]?.avatar
                  ? BASE_URL + inReplyTo.contacts[0].avatar
                  : process.env.PUBLIC_URL + "/assets/img/head.svg"}
                alt=""
              />
            </div>
          )}
          {isOwn && (
            <img
              className="reply-preview__contact-avatar"
              src={inReplyTo?.contacts?.[0]?.avatar
                ? BASE_URL + inReplyTo.contacts[0].avatar
                : process.env.PUBLIC_URL + "/assets/img/head.svg"}
              alt=""
            />
          )}
          <p className={isOwn ? `m-0 simple-msg-${chatId}` : "m-0 simple-msg"}>
            <span className={isOwn ? `${msgClass} reply-preview__contact-name` : msgClass}>
              {inReplyTo?.contacts?.[0]?.fullName}
            </span>
          </p>
        </div>
      )}

      {inReplyTo?.messageType === "map" && (
        <div className="reply-preview__map">
          <LocationMapPreview
            url={decryptedMessage(inReplyTo.message)}
            height={70}
            showLabel={false}
          />
        </div>
      )}

      {inReplyTo?.messageType === "audio" && (
        <div className="pointer" style={{ "--reply-sender-color": senderColor }}>
          <p className="reply-sender-name sender-name">{senderName}</p>
          <div className="voice-message-container">
            <div className="audio-file modern-voice-player">
              <UserAvatar
                src={inReplyTo?.senderAvatar ? BASE_URL + inReplyTo.senderAvatar : ""}
                width={30}
                height={30}
                className="flex-shrink-0"
              />
              <VoiceMessagePlayer
                src={buildImageUrl(inReplyTo?.local, inReplyTo?.fileUrl)}
                audioType={inReplyTo?.fileUrl && inReplyTo.fileUrl.includes(".mp3") ? "audio/mp3" : "audio/wav"}
              />
            </div>
          </div>
          {inReplyTo?.transcribedAudio && (
            <div className="voice-transcript-text reply-preview__transcript">
              {decryptedMessage(inReplyTo.transcribedAudio).length > 80
                ? decryptedMessage(inReplyTo.transcribedAudio).slice(0, 80) + '...'
                : decryptedMessage(inReplyTo.transcribedAudio)}
            </div>
          )}
        </div>
      )}

      {inReplyTo?.messageType === "video" && (
        <ChatVideoBubble
          fileUrl={inReplyTo.fileUrl}
          thumbnailUrl={inReplyTo.thumbnailUrl}
          local={inReplyTo.local}
          attachedFileName={inReplyTo.attachedFileName}
          compact
        />
      )}

      {inReplyTo?.fileUrl && inReplyTo?.messageType === "image" && (
        <div className="reply-preview__image-row">
          <div className="flex-shrink-0">
            <img
              src={buildImageUrl(inReplyTo?.local, inReplyTo?.fileUrl)}
              className="reply-preview__image"
              alt=""
            />
          </div>
          <div
            className="reply-left-image-text reply-preview__image-text"
            style={{ "--reply-sender-color": senderColor }}
          >
            <p className="reply-sender-name sender-name">{senderName}</p>
            {inReplyTo?.message && inReplyTo.message.trim().length > 0 && (
              <span
                className={`${msgClass} reply-preview__image-caption`}
                dangerouslySetInnerHTML={renderHtml(inReplyTo.message)}
              />
            )}
          </div>
        </div>
      )}

      {inReplyTo?.messageType === "doc" && (
        <p className={`m-0 simple-reply-msg-${chatId}`}>
          <span className={msgClass}>
            <AiOutlineFile /> {inReplyTo.attachedFileName}
          </span>
        </p>
      )}

      {inReplyTo?.messageType !== "image" &&
        inReplyTo?.messageType !== "audio" &&
        inReplyTo?.messageType !== "doc" &&
        inReplyTo?.messageType !== "contact" &&
        inReplyTo?.messageType !== "map" &&
        inReplyTo?.messageType !== "video" && (() => {
          const { html, needsToggle } = renderTruncatedHtml(inReplyTo?.message || "");
          return (
            <p className={`m-0 simple-reply-msg-${chatId}`}>
              <span
                className={`${msgClass} reply-preview__text`}
                dangerouslySetInnerHTML={html}
              />
              {needsToggle && (
                <span
                  className="reply-show-more"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(!expanded); }}
                >
                  {expanded ? strings?.see_less || "...less" : strings?.see_more || "...more"}
                </span>
              )}
            </p>
          );
        })()}
    </div>
  );
});

export default ReplyPreview;
